import { app, auth, mustVerifyEmail, mustCreateProfile, checkUserSuspension, guest, setReferralFromQuery } from '@/routes/middlewares';

import { useStore } from '@/store';

import About from '@/views/About.vue';
import App from '@/views/_base/App.vue';
import Auth from '@/views/_base/Auth.vue';
import ChannelsIndex from '@/views/channels/Index.vue';
import ChannelsCreate from '@/views/channels/Create.vue';
import ChannelsEdit from '@/views/channels/_handle_/Edit.vue';
import ChannelsInfo from '@/views/channels/_handle_/Info.vue';
import ChannelsShowBase from '@/views/channels/_handle_/Base.vue';
import ChannelsShow from '@/views/channels/_handle_/Index.vue';
import ChannelsFilter from '@/views/channels/_handle_/Filter.vue';
import ChannelsNotificationSettings from '@/views/channels/_handle_/NotificationSettings.vue';
import CreateProfile from '@/views/CreateProfile.vue';
import ConnectionsIndex from '@/views/connections/Index.vue';
import Guidelines from '@/views/Guidelines.vue';
import Feed from '@/views/Feed.vue';
import FileUploader from '@/views/files/Uploader.vue';
import ForgotPassword from '@/views/Auth/ForgotPassword.vue';
import HelpDeleteAccount from '@/views/Help/DeleteAccount.vue';
import Login from '@/views/Auth/Login.vue';
import Logout from '@/views/Auth/Logout.vue';
import MarketplaceIndex from '@/views/marketplace/Index.vue';
import MarketplaceListingShow from '@/views/marketplace/listings/_id/Index.vue';
import MarketplaceListingEdit from '@/views/marketplace/listings/_id/Edit.vue';
import MarketplaceListingCreate from '@/views/marketplace/listings/create/Index.vue';
import MarketplaceSeller from '@/views/marketplace/sellers/_id/Index.vue';
import MarketplaceSellerCreate from '@/views/marketplace/sellers/Create.vue';
import MarketplaceMeEdit from '@/views/marketplace/me/Edit.vue';
import MarketplaceMeListings from '@/views/marketplace/me/Listings.vue';
import MarketplaceSavedListings from '@/views/marketplace/saved-listings/Index.vue';
import NotFound from '@/components/Errors/NotFound.vue';
import Notifications from '@/views/Notifications.vue';
import PrivacyPolicy from '@/views/PrivacyPolicy.vue';
import Eula from '@/views/Eula.vue';
import ProfileUsernameIndex from '@/views/profiles/_username_/Index.vue';
import ProfileUsernameConnect from '@/views/profiles/_username_/Connect.vue';
import ProfileUsernameDisconnect from '@/views/profiles/_username_/Disconnect.vue';
import PostIndex from '@/views/posts/_id_/Index.vue';
import ProfileReputation from '@/views/profiles/_username_/Reputation.vue';
import PostCreate from '@/views/posts/create/Index.vue';
import ReferralsIndex from '@/views/referrals/Index.vue';
import Register from '@/views/Auth/Register.vue';
import ResetPassword from '@/views/Auth/ResetPassword.vue';
import SearchBase from '@/views/Search/Base.vue';
import SearchChannel from '@/views/Search/Channel.vue';
import SearchPost from '@/views/Search/Post.vue';
import SearchQuestion from '@/views/Search/Question.vue';
import SearchUser from '@/views/Search/User.vue';
import SearchInformationHub from '@/views/Search/InformationHub.vue';
import Settings from '@/views/Settings/Settings.vue';
import SettingsAppearance from '@/views/Settings/Appearance.vue';
import SettingsChangeEmail from '@/views/Settings/ChangeEmail.vue';
import SettingsChangePassword from '@/views/Settings/ChangePassword.vue';
import SettingsActiveSessions from '@/views/Settings/ActiveSessions.vue';
import SettingsDeleteAccount from '@/views/Settings/DeleteAccount.vue';
import SettingsEditProfile from '@/views/Settings/EditProfile.vue';
import SettingsIndex from '@/views/Settings/Index.vue';
import SettingsNotifications from '@/views/Settings/Notifications.vue';
import SettingsJoinedChannels from '@/views/Settings/Channels.vue';
import VerifyEmail from '@/views/VerifyEmail.vue';
import { InformationHubIndex, InformationHubArticle, InformationHubCategory } from '@/views/information-hub';
import BlockedUsersIndex from "@/views/blocked-users/Index.vue";
import Conversations from '@/views/conversations/Index.vue';
import Conversation from '@/views/conversations/_id/Index.vue';
import ConversationCreate from '@/views/conversations/Create.vue';
import ConversationInfo from '@/views/conversations/_id/Info.vue';
import Landing from '@/views/Landing.vue';

function keepDefaultView(to, from) {
    const store = useStore();
    const toMatched = to.matched[to.matched.length - 1];
    const fromMatched = from.matched[from.matched.length - 1];

    if (fromMatched) {
        toMatched.components.default = fromMatched.components.default;
    } else if (store.state.user) {
        toMatched.components.default = Feed;
    }
}

const applyToRoutes = ({ middlewares }, routes) => {
    return routes.map(route => {
        route.meta = route.meta ?? {};
        route.meta.middlewares = route.meta.middlewares ?? [];

        if (!Array.isArray(route.meta.middlewares)) {
            route.meta.middlewares = [route.meta.middlewares];
        }

        route.meta.middlewares = [...middlewares, ...route.meta.middlewares];

        if (route.children && Array.isArray(route.children)) {
            route.children = applyToRoutes({ middlewares: route.meta.middlewares }, route.children);
        }

        return route;
    });
}

export default applyToRoutes({
    middlewares: [app],
}, [
    {
        path: '',
        component: Landing,
        name: 'landing',
        meta: {
            middlewares: [guest],
        }
    },
    {
        path: '',
        component: App,
        children: [
            ...applyToRoutes({
                middlewares: [mustVerifyEmail, mustCreateProfile, checkUserSuspension],
            }, [
                {
                    path: 'help/delete-account',
                    component: HelpDeleteAccount,
                    name: 'help.delete-account',
                },
                {
                    path: 'about',
                    component: About,
                    name: 'about',
                },
                {
                    path: 'guidelines',
                    component: Guidelines,
                    name: 'guidelines',
                },
                {
                    path: 'agreements/privacy-policy',
                    component: PrivacyPolicy,
                    name: 'agreements.privacy-policy',
                },
                {
                    path: 'agreements/eula',
                    component: Eula,
                    name: 'agreements.eula',
                },
                {
                    path: 'information-hub',
                    name: 'information-hub',
                    component: InformationHubIndex,
                    children: [
                        {
                            path: '',
                            name: 'information-hub.index',
                            meta: {
                                showNav: true,
                            }
                        },
                    ],
                },
                {
                    path: 'information-hub/:category',
                    component: InformationHubCategory,
                    name: 'information-hub.category',
                    meta: {
                        showNav: true,
                    },
                    children: [
                        {
                            path: ':id',
                            component: InformationHubArticle,
                            name: 'information-hub.article',
                        },
                    ],
                },
                {
                    path: 'posts/:id',
                    component: PostIndex,
                    name: 'posts.show',
                    children: [
                        {
                            path: 'file-uploader',
                            component: FileUploader,
                            meta: {
                                middlewares: [auth],
                            },
                        },
                    ],
                },
                {
                    path: 'marketplace/listings/:id',
                    component: MarketplaceListingShow,
                    name: 'marketplace.listing.show',
                },
                {
                    path: 'marketplace/sellers/:id',
                    component: MarketplaceSeller,
                    name: 'marketplace.sellers.show',
                    meta: {
                        componentKey: 'id',
                    },
                },
                {
                    path: 'platoon:platoon',
                    redirect: to => {
                        localStorage.setItem('platoon', to.params.platoon);

                        return { path: '/auth/register' };
                    }
                },
                {
                    path: 'amb/:code',
                    redirect: to => {
                        localStorage.setItem('referral_code', to.params.code);

                        return { path: '/auth/register' };
                    }
                },
            ]),

            {
                path: 'onboarding/create-profile',
                name: 'create-profile',
                component: CreateProfile,
                meta: {
                    middlewares: [auth, mustVerifyEmail, function ({ store }) {
                        if (store.state.user && store.state.user.profile) {
                            return {
                                name: 'feed',
                            }
                        }
                    }],
                },
            },
            // requires login
            ...applyToRoutes({
                middlewares: [auth, mustVerifyEmail, mustCreateProfile, checkUserSuspension],
            }, [
                {
                    path: 'feed',
                    name: 'feed',
                    component: Feed,
                    meta: {
                        showNav: true,
                        middlewares: [
                            setReferralFromQuery,
                        ]
                    },
                    children: [
                        {
                            path: 'file-uploader',
                            component: FileUploader,
                        },
                    ],
                },
                {
                    path: 'marketplace',
                    children: [
                        {
                            path: '',
                            component: MarketplaceIndex,
                            name: 'marketplace',
                            meta: {
                                showNav: true,
                            }
                        },
                        {
                            path: 'listings/create',
                            component: MarketplaceListingCreate,
                            name: 'marketplace.listing.create',
                        },
                        {
                            path: 'listings/:id/edit',
                            component: MarketplaceListingEdit,
                            name: 'marketplace.listing.edit',
                        },
                        {
                            path: 'sellers/create',
                            name: 'marketplace.sellers.create',
                            component: MarketplaceSellerCreate
                        },
                        {
                            path: 'me/edit',
                            name: 'marketplace.me.edit',
                            component: MarketplaceMeEdit
                        },
                        {
                            path: 'me/listings',
                            name: 'marketplace.me.listings',
                            component: MarketplaceMeListings
                        },
                        {
                            path: 'saved-listings',
                            name: 'marketplace.saved-listings',
                            component: MarketplaceSavedListings
                        },
                    ]
                },
                {
                    path: 'posts/create',
                    component: PostCreate,
                    name: 'posts.create',
                    children: [
                        {
                            path: 'file-uploader',
                            component: FileUploader,
                        },
                    ],
                },
                {
                    path: 'channels',
                    component: ChannelsIndex,
                    name: 'channels.index',
                    meta: {
                        showNav: true,
                    },
                    children: [
                        {
                            path: 'create',
                            component: ChannelsCreate,
                            name: 'channels.create',
                        },
                    ],
                },
                {
                    path: 'notifications',
                    component: Notifications,
                    meta: {
                        showNav: true,
                    },
                    name: 'notifications',
                },
                {
                    path: 'channels/:handle',
                    component: ChannelsShowBase,
                    children: [
                        {
                            path: '',
                            component: ChannelsShow,
                            name: 'channels.show',
                        },
                        {
                            path: 'filter',
                            component: ChannelsFilter,
                            name: 'channels.show.filter',
                        },
                        {
                            path: 'info',
                            component: ChannelsInfo,
                            name: 'channels.info',
                        },
                        {
                            path: 'edit',
                            component: ChannelsEdit,
                            name: 'channels.edit',
                        },
                        {
                            path: 'notification-settings',
                            component: ChannelsNotificationSettings,
                            name: 'channels.notification-settings',
                        }
                    ],
                },
                {
                    path: 'profiles/:username',
                    component: ProfileUsernameIndex,
                    name: 'profiles.show',
                    children: [
                        {
                            path: 'connect',
                            component: ProfileUsernameConnect,
                            name: 'profiles.show.connect',
                        },
                        {
                            path: 'disconnect',
                            component: ProfileUsernameDisconnect,
                            name: 'profiles.show.disconnect',
                        },
                        {
                            path: 'reputation',
                            component: ProfileReputation,
                            name: 'profiles.show.reputation',
                        }
                    ],
                },
                {
                    path: 'connections',
                    component: ConnectionsIndex,
                    name: 'connections.index',
                },
                {
                    path: 'referrals',
                    component: ReferralsIndex,
                    name: 'referrals.index',
                },
                {
                    path: '/conversations',
                    component: Conversations,
                    name: 'conversations',
                    meta: {
                        hideSidebar: true,
                        showNav: true,
                        navIsCollapsed: true,
                    },
                    children: [
                        {
                            path: 'create',
                            component: ConversationCreate,
                            name: 'conversation.create',
                            meta: {
                                showNav: false,
                            },
                        },
                        {
                            path: ':id',
                            component: Conversation,
                            name: 'conversation',
                            meta: {
                                showNav: false,
                                componentKey: 'id',
                            },
                            children: [
                                {
                                    path: 'info',
                                    component: ConversationInfo,
                                    name: 'conversation.info',
                                },
                            ],
                        },
                    ],
                },
                {
                    path: 'search',
                    component: SearchBase,
                    children: [
                        {
                            path: 'post',
                            component: SearchPost,
                            name: 'search.post',
                        },
                        {
                            path: 'channel',
                            component: SearchChannel,
                            name: 'search.channel',
                        },
                        {
                            path: 'question',
                            component: SearchQuestion,
                            name: 'search.question',
                        },
                        {
                            path: 'user',
                            component: SearchUser,
                            name: 'search.user',
                        },
                        {
                            path: 'information-hub',
                            component: SearchInformationHub,
                            name: 'search.information-hub',
                        },
                    ]
                },
                {
                    path: 'settings',
                    component: Settings,
                    children: [
                        {
                            path: '',
                            component: SettingsIndex,
                            name: 'settings.index',
                            meta: {
                                middlewares: [
                                    function ({ store }) {
                                        if (store.state.deviceType === 'desktop') {
                                            return {
                                                name: 'settings.edit-profile'
                                            }
                                        }
                                    },
                                ]
                            }
                        },
                        {
                            path: 'appearance',
                            component: SettingsAppearance,
                            name: 'settings.appearance',
                        },
                        {
                            path: 'delete-account',
                            component: SettingsDeleteAccount,
                            name: 'settings.delete-account',
                        },
                        {
                            path: 'change-email',
                            component: SettingsChangeEmail,
                            name: 'settings.change-email',
                        },
                        {
                            path: 'change-password',
                            component: SettingsChangePassword,
                            name: 'settings.change-password',
                        },
                        {
                            path: 'sessions',
                            component: SettingsActiveSessions,
                            name: 'settings.sessions',
                        },
                        {
                            path: 'edit-profile',
                            component: SettingsEditProfile,
                            name: 'settings.edit-profile',
                        },
                        {
                            path: 'notifications',
                            component: SettingsNotifications,
                            name: 'settings.notifications',
                        },
                        {
                            path: 'notifications/channels',
                            component: SettingsJoinedChannels,
                            name: 'settings.notifications.channels',
                        },
                    ],
                },
                {
                    path: 'blocked-users',
                    component: BlockedUsersIndex,
                    name: 'blocked-users.index',
                },
            ])
        ],
    },
    {
        path: '/webinar',
        component: Auth,
        name: 'webinar',
        children: [
            {
                path: '',
                component: Register,
                name: 'webinar.register',
            },
        ],
    },
    {
        path: '/auth',
        component: Auth,
        children: [
            ...applyToRoutes({
                middlewares: [guest],
            }, [
                {
                    path: 'forgot-password',
                    component: ForgotPassword,
                    name: 'forgot-password',
                },
                {
                    path: 'reset-password/:token',
                    component: ResetPassword,
                    name: 'reset-password',
                },
                {
                    path: 'login',
                    component: Login,
                    name: 'login',
                },
                {
                    path: 'register',
                    component: Register,
                    name: 'register',
                    meta: {
                        middlewares: [setReferralFromQuery]
                    }
                },
            ]),
            {
                path: 'logout',
                name: 'logout',
                component: Logout,
            },
        ]
    },
    {
        path: '/verify-email',
        component: VerifyEmail,
        name: 'verify-email',
        meta: {
            middlewares: [auth],
        },
    },
    { path: '/:pathMatch(.*)*', name: 'errors.404', component: NotFound },
]);
